/// Model list fetching, caching, curated recommendations, and free/paid filter
/// for OpenRouter LLM models.

import type { OpenRouterModel } from "@/types/ai";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODELS_API_URL = "https://openrouter.ai/api/v1/models";

/** Curated list of recommended model IDs shown at the top of the picker. */
export const RECOMMENDED_MODELS: readonly string[] = [
  "nvidia/nemotron-3-nano-30b-a3b:free",
  "stepfun/step-3.5-flash:free",
  "anthropic/claude-sonnet-4.6",
  "openai/gpt-4o",
  "google/gemini-2.0-flash-001",
  "deepseek/deepseek-chat-v3-0324",
] as const;

// ---------------------------------------------------------------------------
// Model fetching
// ---------------------------------------------------------------------------

/** Filter options for the model list. */
interface ModelFilterOptions {
  freeOnly?: boolean;
  searchQuery?: string;
}

/**
 * Fetch the full list of available models from OpenRouter.
 * Requires a valid API key for authentication.
 */
export async function fetchModels(apiKey: string): Promise<OpenRouterModel[]> {
  const response = await fetch(MODELS_API_URL, {
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.status}`);
  }

  const data = (await response.json()) as { data: OpenRouterModel[] };
  return data.data;
}

// ---------------------------------------------------------------------------
// Model filtering
// ---------------------------------------------------------------------------

/**
 * Filter a model list by free-only and/or search query.
 *
 * A model is considered free when both prompt and completion pricing are "0".
 * Search matches model name case-insensitively.
 */
export function filterModels(
  models: OpenRouterModel[],
  options: ModelFilterOptions,
): OpenRouterModel[] {
  let filtered = models;

  if (options.freeOnly) {
    filtered = filtered.filter((m) => m.pricing.prompt === "0" && m.pricing.completion === "0");
  }

  if (options.searchQuery) {
    const query = options.searchQuery.toLowerCase();
    filtered = filtered.filter((m) => m.name.toLowerCase().includes(query));
  }

  return filtered;
}

// ---------------------------------------------------------------------------
// Model sorting
// ---------------------------------------------------------------------------

/**
 * Sort models with recommended models first, then alphabetical by name.
 * Recommended models appear in the order defined by the recommendedIds array.
 */
export function sortModelsWithRecommended(
  models: OpenRouterModel[],
  recommendedIds: readonly string[],
): OpenRouterModel[] {
  const recommendedSet = new Set(recommendedIds);
  const recommendedOrder = new Map(recommendedIds.map((id, i) => [id, i]));

  const recommended: OpenRouterModel[] = [];
  const rest: OpenRouterModel[] = [];

  for (const model of models) {
    if (recommendedSet.has(model.id)) {
      recommended.push(model);
    } else {
      rest.push(model);
    }
  }

  // Sort recommended by their position in the curated list
  recommended.sort((a, b) => (recommendedOrder.get(a.id) ?? 0) - (recommendedOrder.get(b.id) ?? 0));

  // Sort the rest alphabetically by name
  rest.sort((a, b) => a.name.localeCompare(b.name));

  return [...recommended, ...rest];
}

/**
 * Check whether a model is free (zero prompt and completion cost).
 */
export function isModelFree(model: OpenRouterModel): boolean {
  return model.pricing.prompt === "0" && model.pricing.completion === "0";
}
